import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Image, Pencil, X, Check, ArrowLeft, Filter } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = ["Construction", "Rénovation", "Toiture", "Plomberie", "Électricité", "Terrassement"];
const placements = [
  { value: "gallery", label: "Galerie Réalisations" },
  { value: "service-construction", label: "Carte Service: Construction" },
  { value: "service-renovation", label: "Carte Service: Rénovation" },
  { value: "service-toiture", label: "Carte Service: Toiture" },
  { value: "service-plomberie", label: "Carte Service: Plomberie" },
  { value: "service-electricite", label: "Carte Service: Électricité" },
  { value: "service-terrassement", label: "Carte Service: Terrassement" },
  { value: "hero-accueil", label: "Hero: Page Accueil" },
  { value: "hero-apropos", label: "Hero: Page À propos" },
  { value: "hero-services", label: "Hero: Page Services" },
  { value: "hero-contact", label: "Hero: Page Contact" },
  { value: "hero-zones", label: "Hero: Page Zones" },
];

export default function AdminPhotos() {
  const { user, token, loading: authLoading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [filterPlace, setFilterPlace] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadCat, setUploadCat] = useState("");
  const [uploadPlace, setUploadPlace] = useState("gallery");
  const [uploadCity, setUploadCity] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [autoClassify, setAutoClassify] = useState(false);

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);

  const fetchPhotos = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCat !== "all") params.set("category", filterCat);
      if (filterPlace !== "all") params.set("placement", filterPlace);
      const res = await axios.get(`${API}/photos?${params}`, { headers: authHeaders });
      setPhotos(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (token) fetchPhotos(); }, [token, filterCat, filterPlace]);

  const compressFile = (file) => new Promise((resolve) => {
    if (file.size < 9 * 1024 * 1024) { resolve(file); return; }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ratio = Math.min(2000 / img.width, 2000 / img.height, 1);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: "image/jpeg" })), "image/jpeg", 0.8);
    };
    img.src = URL.createObjectURL(file);
  });

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let done = 0;
    for (let i = 0; i < files.length; i++) {
      setUploadProgress(`Upload ${i + 1}/${files.length}...`);
      const compressed = await compressFile(files[i]);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("category", uploadCat);
      fd.append("placement", uploadPlace);
      fd.append("city", uploadCity);
      fd.append("auto_classify", autoClassify ? "true" : "false");
      fd.append("title", files[i].name.replace(/\.[^.]+$/, ""));
      try {
        await axios.post(`${API}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" }, timeout: 60000 });
        done++;
      } catch (err) { console.error(err); }
    }
    setUploadProgress(`${done}/${files.length} photos uploadees !`);
    setUploading(false);
    fetchPhotos();
    if (fileRef.current) fileRef.current.value = "";
    setTimeout(() => setUploadProgress(""), 3000);
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/photos/${id}`, { headers: authHeaders });
      setPhotos(p => p.filter(x => x.id !== id));
    } catch (e) { console.error(e); }
  };

  const startEdit = (photo) => {
    setEditing(photo.id);
    setEditForm({ title: photo.title, category: photo.category, placement: photo.placement, city: photo.city || "" });
    setTimeout(() => {
      const el = document.getElementById(`photo-card-${photo.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/photos/${id}`, editForm, { headers: authHeaders });
      setEditing(null);
      fetchPhotos();
    } catch (e) { console.error(e); }
  };

  if (authLoading) return <div style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#aaa' }}>Chargement...</p></div>;
  if (!user) return null;

  return (
    <div data-testid="admin-photos" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', color: '#fff' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#141414', borderBottom: '1px solid #27272A', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate("/admin/dashboard")} style={{ color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
            <ArrowLeft size={18} /> Dashboard
          </button>
          <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '22px', fontWeight: 900, margin: 0 }}>
            <Image size={22} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />Gestion Photos
          </h1>
          <span style={{ backgroundColor: '#8B0000', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '4px' }}>{photos.length} photos</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setShowUpload(!showUpload)} style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 700, fontSize: '13px', padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Upload size={16} /> Uploader des photos
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Upload Panel */}
        {showUpload && (
          <div style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '12px', padding: '28px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Uploader des photos</h2>
              <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Catégorie *</label>
                <select value={uploadCat} onChange={e => setUploadCat(e.target.value)} style={{ backgroundColor: '#0A0A0A', border: '1px solid #27272A', color: '#fff', padding: '10px 14px', width: '100%', borderRadius: '4px' }}>
                  <option value="">Sélectionnez...</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Emplacement *</label>
                <select value={uploadPlace} onChange={e => setUploadPlace(e.target.value)} style={{ backgroundColor: '#0A0A0A', border: '1px solid #27272A', color: '#fff', padding: '10px 14px', width: '100%', borderRadius: '4px' }}>
                  {placements.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Ville (optionnel)</label>
                <input value={uploadCity} onChange={e => setUploadCity(e.target.value)} placeholder="Ex: Ixelles" style={{ backgroundColor: '#0A0A0A', border: '1px solid #27272A', color: '#fff', padding: '10px 14px', width: '100%', borderRadius: '4px' }} />
              </div>
            </div>
            <div style={{ border: '2px dashed #27272A', borderRadius: '8px', padding: '40px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.3s' }} onClick={() => fileRef.current?.click()}>
              <Upload size={32} color="#8B0000" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: '#aaa', fontSize: '14px', margin: '0 0 4px' }}>Cliquez ou glissez vos photos ici</p>
              <p style={{ color: '#666', fontSize: '12px', margin: 0 }}>JPG, PNG, WEBP — Plusieurs fichiers acceptés</p>
              <input ref={fileRef} type="file" multiple accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
            </div>
            {uploading && <p style={{ color: '#8B0000', fontSize: '14px', fontWeight: 700, marginTop: '12px', textAlign: 'center' }}>{uploadProgress}</p>}
            {!uploading && uploadProgress && <p style={{ color: '#4ade80', fontSize: '14px', fontWeight: 700, marginTop: '12px', textAlign: 'center' }}>{uploadProgress}</p>}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
          <Filter size={16} color="#8B0000" />
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ backgroundColor: '#141414', border: '1px solid #27272A', color: '#fff', padding: '8px 14px', borderRadius: '4px', fontSize: '13px' }}>
            <option value="all">Toutes catégories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterPlace} onChange={e => setFilterPlace(e.target.value)} style={{ backgroundColor: '#141414', border: '1px solid #27272A', color: '#fff', padding: '8px 14px', borderRadius: '4px', fontSize: '13px' }}>
            <option value="all">Tous emplacements</option>
            {placements.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {/* Photo Grid */}
        {photos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#666' }}>
            <Image size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ fontSize: '16px' }}>Aucune photo. Cliquez "Uploader des photos" pour commencer.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
            {photos.map(photo => (
              <div key={photo.id} id={`photo-card-${photo.id}`} style={{ backgroundColor: '#141414', border: editing === photo.id ? '2px solid #8B0000' : '1px solid #27272A', borderRadius: '8px', overflow: 'hidden', transition: 'border-color 0.3s', scrollMarginTop: '100px' }}>
                <div style={{ position: 'relative', aspectRatio: '4/3', overflow: 'hidden' }}>
                  <img src={photo.image_url} alt={photo.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px' }}>
                    <button onClick={() => startEdit(photo)} style={{ backgroundColor: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#fff' }}><Pencil size={14} /></button>
                    <button onClick={() => deletePhoto(photo.id)} style={{ backgroundColor: 'rgba(139,0,0,0.8)', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#fff' }}><Trash2 size={14} /></button>
                  </div>
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', gap: '4px' }}>
                    <span style={{ backgroundColor: '#8B0000', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>{photo.category}</span>
                  </div>
                </div>
                {editing === photo.id ? (
                  <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} style={{ backgroundColor: '#0A0A0A', border: '1px solid #27272A', color: '#fff', padding: '6px 10px', borderRadius: '4px', fontSize: '13px' }}>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={editForm.placement} onChange={e => setEditForm(f => ({ ...f, placement: e.target.value }))} style={{ backgroundColor: '#0A0A0A', border: '1px solid #27272A', color: '#fff', padding: '6px 10px', borderRadius: '4px', fontSize: '13px' }}>
                      {placements.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => saveEdit(photo.id)} style={{ flex: 1, backgroundColor: '#8B0000', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Check size={12} /> Sauver</button>
                      <button onClick={() => setEditing(null)} style={{ flex: 1, backgroundColor: '#27272A', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px', cursor: 'pointer', fontSize: '12px' }}>Annuler</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px' }}>
                    <span style={{ backgroundColor: '#8B0000', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>{photo.category || "—"}</span>
                    <span style={{ color: '#666', fontSize: '11px', marginLeft: '8px' }}>{placements.find(p => p.value === photo.placement)?.label || ""}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
